// Firebase-Auth-Helper für den Admin-Login-Flow mit MFA.
//
// Flow:
// 1. signInWithEmailAndPasswordOrMfa() → entweder direkt erfolgreich (kein MFA),
//    oder wirft MfaRequiredError mit resolver für die TOTP-Challenge.
// 2. completeTotpChallenge(resolver, code) → schließt MFA ab, gibt User zurück.
// 3. getFreshIdToken() → holt einen neuen ID-Token mit den 2FA-Claims.

import {
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  type MultiFactorResolver,
  type TotpSecret,
  type UserCredential,
} from 'firebase/auth';
import { getFirebaseAuth } from '../../lib/firebase';

export class MfaRequiredError extends Error {
  constructor(public readonly resolver: MultiFactorResolver) {
    super('Multi-factor authentication required');
    this.name = 'MfaRequiredError';
  }
}

interface FirebaseAuthErrorLike {
  code?: string;
}

function isMultiFactorErrorCode(err: unknown): boolean {
  return (err as FirebaseAuthErrorLike).code === 'auth/multi-factor-auth-required';
}

export async function signInWithEmailOrThrowMfa(
  email: string,
  password: string,
): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    if (isMultiFactorErrorCode(err)) {
      const resolver = getMultiFactorResolver(auth, err as never);
      throw new MfaRequiredError(resolver);
    }
    throw err;
  }
}

export async function completeTotpChallenge(
  resolver: MultiFactorResolver,
  totpCode: string,
): Promise<UserCredential> {
  // Aktuell unterstützen wir nur TOTP (Authenticator-App). SMS könnten wir
  // später ergänzen, wenn Bedarf — TOTP ist sicherer und ohne Drittanbieter-
  // Kosten.
  const totpFactor = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!totpFactor) {
    throw new Error('No TOTP factor configured for this account');
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpFactor.uid, totpCode);
  return resolver.resolveSignIn(assertion);
}

export async function getFreshIdToken(): Promise<string> {
  const auth = getFirebaseAuth();
  if (!auth.currentUser) {
    throw new Error('No Firebase user signed in');
  }
  // forceRefresh=true holt einen neuen Token mit aktuellen Claims (inkl. 2FA).
  return auth.currentUser.getIdToken(true);
}

export async function signOutFirebase(): Promise<void> {
  const auth = getFirebaseAuth();
  await auth.signOut();
}

// ── MFA-Enrollment ─────────────────────────────────────────
//
// Wird auf der EnrollMfaPage genutzt, nachdem der User mit Email/Passwort
// signed-in ist (ohne MFA — daher hat Backend mit 403 abgelehnt).
//
// Flow:
//   1. setupTotpEnrollment() generiert TOTP-Secret + otpauth-URL für QR
//   2. User scant QR mit Authenticator-App, gibt 6-stelligen Code ein
//   3. completeTotpEnrollment(secret, code) ruft Firebase enroll() auf
//   4. Frontend logged out + redirected zu /login (User loggt erneut ein,
//      diesmal mit MFA-Challenge → ID-Token enthält second_factor)

export interface TotpEnrollment {
  secret: TotpSecret;
  qrUrl: string;
  manualSecret: string;
}

export async function setupTotpEnrollment(): Promise<TotpEnrollment> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Nicht angemeldet — bitte erneut einloggen.');

  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);

  const accountName = user.email ?? user.uid;
  const issuer = 'Currivium Admin';
  const qrUrl = secret.generateQrCodeUrl(accountName, issuer);

  return { secret, qrUrl, manualSecret: secret.secretKey };
}

export async function completeTotpEnrollment(
  secret: TotpSecret,
  code: string,
  displayName = 'Authenticator',
): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Nicht angemeldet — bitte erneut einloggen.');

  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(user).enroll(assertion, displayName);
}
